﻿import { useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useToasts } from "react-toast-notifications";
import fileDownload from "js-file-download";

import useListController from "@/hooks/list/useListController";
import {
	useInventoryTakingSessions,
	useInventoryTakingLookups,
	useInventoryTakingResults,
	useFinalizeInventoryTaking,
	useExportInventoryTaking,
} from "@/hooks/inventoryTaking/useInventoryTaking";
import { useScreenPermissions } from "@/hooks/general/useScreenPermissions";
import useFilterStore from "@/stores/useFilterStore";
import { SCREEN_ID_INVENTORY_TAKING } from "@/constants/screenIds";

import CustomFetching from "@/components/desktop/fetching/CustomFetching";
import ListScaffold from "@/components/desktop/list/table/ListScaffold";
import CustomNoPermissionView from "@/components/desktop/permission/no-permission/CustomNoPermissionView";
import CustomConfirmModal from "@/components/desktop/modal/CustomConfirmModal";

import { useInventoryTakingFilters } from "./hooks/useInventoryTakingFilters";
import { useInventoryTakingColumns } from "./hooks/useInventoryTakingColumns";
import InventoryTakingDetails from "./InventoryTakingDetails";

import { toast } from "@/services/Utils";

import { TAKING_FILTERS, TAKING_METADATA, TAKING_BASE_TABLE } from "./inventory-taking.constants";

const InventoryTaking = () => {
	const navigate = useNavigate();
	const { addToast } = useToasts();
	const { filter } = useFilterStore();
	const { estate_id } = filter;

	const { canShow, isLoading: isLoadingPermissions } = useScreenPermissions(SCREEN_ID_INVENTORY_TAKING);
	const canLoadScreenData = !isLoadingPermissions && canShow;

	const [confirmSession, setConfirmSession] = useState(null);

	const finalizeMutation = useFinalizeInventoryTaking();
	const exportMutation = useExportInventoryTaking();

	const {
		isFilterOpen,
		filtersState,
		appliedFiltersState,
		tableState,
		appliedFilterParams,
		reactTableSort,
		openFilter,
		closeFilter,
		handleFilterChange,
		handleApplyFilters,
		handleClearAllFilters,
		handleClearOneFilter,
		handlePageChange,
		handlePageSizeChange,
		handleSortByChange,
		setFilterOptions,
	} = useListController({
		baseFilter: TAKING_FILTERS,
		baseTable: TAKING_BASE_TABLE,
	});

	const { data: lookups, isLoading: isLoadingLookups } = useInventoryTakingLookups({
		enabled: canLoadScreenData,
	});

	const { isReady } = useInventoryTakingFilters({
		lookups,
		filtersState,
		setFilterOptions,
		handleFilterChange,
		handleApplyFilters,
	});

	const filters = useMemo(
		() => ({
			...appliedFilterParams,
			...tableState,
			estate_id: estate_id || "",
		}),
		[appliedFilterParams, tableState, estate_id],
	);

	const { data, isLoading, isFetching } = useInventoryTakingSessions(filters, {
		enabled: canLoadScreenData && !isLoadingLookups && Boolean(lookups) && isReady,
	});

	const handleFinalize = useCallback((session) => {
		setConfirmSession(session);
	}, []);

	const handleConfirmFinalize = useCallback(() => {
		if (!confirmSession) return;

		finalizeMutation.mutate(confirmSession.id, {
			onSuccess: () => {
				toast(addToast, "La toma de inventario fue finalizada correctamente", "success");
				setConfirmSession(null);
			},
			onError: () => {
				toast(addToast, "Error al finalizar la toma de inventario", "error");
			},
		});
	}, [confirmSession, finalizeMutation, addToast]);

	const handleExport = useCallback(
		(session) => {
			exportMutation.mutate(session.id, {
				onSuccess: (response) => {
					const fileName = `toma_inventario_${session.id}.xlsx`;
					fileDownload(response.data, fileName);
				},
				onError: () => {
					toast(addToast, "Error al exportar la toma de inventario", "error");
				},
			});
		},
		[exportMutation, addToast],
	);

	const columns = useInventoryTakingColumns(tableState, {
		onFinalize: handleFinalize,
		onExport: handleExport,
	});

	const renderExpandedContent = (row) => {
		return <ExpandedRow sessionId={row.original.id} />;
	};

	if (isLoadingPermissions) {
		return <CustomFetching center message="Validando permisos..." />;
	}

	if (!canShow) {
		return <CustomNoPermissionView />;
	}

	return (
		<>
			<ListScaffold
				titleBreadcrumbs={[{ label: "Dashboard", onClick: () => navigate("/") }, { label: TAKING_METADATA.breadcrumb }]}
				subtitle={TAKING_METADATA.subtitle}
				filterTitle={`Filtrar ${TAKING_METADATA.title}`}
				isFilterOpen={isFilterOpen}
				filters={filtersState}
				appliedFilters={appliedFiltersState}
				onFilterChange={handleFilterChange}
				onApplyFilters={handleApplyFilters}
				onClearFilters={handleClearAllFilters}
				onClearOneFilter={handleClearOneFilter}
				onOpenFilter={openFilter}
				onCloseFilter={closeFilter}
				isFetching={isFetching}
				isLoading={isLoading || isLoadingLookups}
				data={data?.data || []}
				totalItems={data?.totalRecords || 0}
				totalPages={data?.totalPages || 0}
				toItem={data?.data?.length || 0}
				fromItem={data?.page === 1 ? 1 : (data?.page - 1) * tableState.per_page + 1}
				pageIndex={tableState.page}
				columns={columns}
				onPageChange={handlePageChange}
				pageSize={tableState.per_page}
				onPageSizeChange={handlePageSizeChange}
				sortBy={reactTableSort}
				onSortByChange={handleSortByChange}
				pageSizeOptions={[20, 50, 100]}
				initialPageSize={tableState.per_page}
				initialSortBy={reactTableSort}
				compactHeaders={true}
				compactBody={true}
				useExpandableRows={true}
				renderExpandedContent={renderExpandedContent}
			/>

			<CustomConfirmModal
				open={!!confirmSession}
				onClose={() => setConfirmSession(null)}
				onConfirm={handleConfirmFinalize}
				title="Finalizar Toma de Inventario"
				message={`¿Está seguro de que desea finalizar la toma de inventario de la bodega "${confirmSession?.estate_name || ""}"? Esta acción comparará los códigos escaneados con el inventario esperado.`}
				confirmText="Sí, finalizar"
				cancelText="No, cancelar"
				isLoading={finalizeMutation.isLoading}
			/>
		</>
	);
};

const ExpandedRow = ({ sessionId }) => {
	const { data, isLoading } = useInventoryTakingResults(sessionId);

	return <InventoryTakingDetails sessionId={sessionId} results={data?.data || []} isLoading={isLoading} />;
};

export default InventoryTaking;
